import React, { useEffect, useRef, useCallback } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { MarkerClusterer } from "@googlemaps/markerclusterer";

const mapContainerStyle = {
  width: '100%',
  height: 'calc( 100vh - 197px',
  backgroundColor: '#ffffff'
};

const defaultOptions = {
  mapTypeId: 'roadmap',
  mapTypeControl: false,
  mapTypeControlOptions: {
    mapTypeIds: ["hybrid", "satellite", "terrain", "roadmap"],
  },
  disableDefaultUI: true,
  zoomControl: true,
  maxZoom: 16,
  minZoom: 5,
  styles: [
    { featureType: "poi.business", stylers: [{ visibility: "off" }] },
    { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] }
  ]
};

const CustomMap = ({ points }) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY
  });
  console.log({googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY})

  const mapRef = useRef(null);
  const clustererRef = useRef(null);
  const polylineRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current && points.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        points.forEach(p => bounds.extend({ lat: parseFloat(p[0]), lng: parseFloat(p[1]) }));
        mapRef.current.fitBounds(bounds);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [points]);

  const renderMapElements = useCallback((map) => {
    if (!points || !Array.isArray(points) || points.length === 0) {
      console.log("No points array provided to map yet", points);
      return;
    }

    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    if (polylineRef.current) polylineRef.current.setMap(null);
    if (clustererRef.current) clustererRef.current.clearMarkers();

    const bounds = new window.google.maps.LatLngBounds();
    const gpoints = [];

    const newMarkers = points.map((p) => {
      const lat = parseFloat(p[0]);
      const lng = parseFloat(p[1]);
      const weight = parseInt(p[2]);
      const loc = new window.google.maps.LatLng(lat, lng);

      bounds.extend(loc);
      gpoints.push(loc);

      const marker = new window.google.maps.Marker({
        position: loc,
        title: `${weight}kg max`,
        label: { color: '#fff', fontSize: '14px', text: String(weight) },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 15,
          fillColor: "#F00",
          fillOpacity: 1,
          strokeWeight: 0.4
        },
        zIndex: weight
      });
      marker.set("weight", weight);
      return marker;
    });

    markersRef.current = newMarkers;

    const polyline = new window.google.maps.Polyline({
      path: gpoints,
      icons: [{
        icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW },
        offset: "100px", repeat: "200px",
      }],
      strokeColor: '#FF0000',
      strokeOpacity: 0.8,
      strokeWeight: 2
    });
    polyline.setMap(map);
    polylineRef.current = polyline;

    const clusterOptions = {
      render({ count, position, markers }) {
        let maxWeight = 0;
        markers.forEach(m => {
          const w = parseFloat(m.get("weight"));
          if (w > maxWeight) maxWeight = w;
        });

        return new window.google.maps.Marker({
          position,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 18,
            fillColor: "#F00",
            fillOpacity: 1,
            strokeWeight: 0.6
          },
          label: { text: String(maxWeight), color: 'white', fontSize: '14px' },
          title: `${maxWeight}kg max`
        });
      }
    };

    clustererRef.current = new MarkerClusterer({
      map,
      markers: newMarkers,
      renderer: clusterOptions
    });

    map.fitBounds(bounds);

  }, [points]);

  useEffect(() => {
    if (isLoaded && mapRef.current) {
      renderMapElements(mapRef.current);
    }
  }, [isLoaded, points, renderMapElements]);

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={{ lat: 39, lng: 22 }}
      zoom={6}
      options={defaultOptions}
      onLoad={onLoad}
    />
  ) : <div style={mapContainerStyle}></div>;
};

export default CustomMap;
